import React from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { validate as isValidUUID, version as getUUIDVersion } from "uuid";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import Modal from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import Alert from "@amzn/meridian/alert";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";

import {
  GATE_LANE_KEYS,
  GATE_LANE_PURPOSE,
  GATE_LANE_PURPOSE_LIST,
  GATE_TABLE,
  R4G_MODE_LIST,
} from "app-constants";
import { keys } from "i18n";
import { equalsIgnoreCase, zipWithFlattenedKeys, getTempId } from "helpers";
import { gateLane } from "types";

const prepareFields = () => {
  return zipWithFlattenedKeys(GATE_TABLE.keys);
};

class GateDialog extends React.PureComponent {
  state = {
    isLabelUsed: false,
    fields: prepareFields(),
  };

  getInitialValues = (gateLane) => {
    if (gateLane) {
      // displaying the formatted phone number after opening the dialog
      const intercomPhoneNumber = !isEmpty(gateLane.intercomPhoneNumber)
        ? parsePhoneNumber(gateLane.intercomPhoneNumber).formatInternational()
        : null;
      return {
        ...gateLane,
        intercomPhoneNumber: intercomPhoneNumber,
      };
    }
    return {
      // Give temporary id for objects added directly with the UI
      id: getTempId(),
      laneIdentifier: "",
      gateId: getTempId(),
      gateIdentifier: "",
      label: "",
      purpose: "",
      r4gMode: "",
      kfbAccessPointId: "",
      intercomPhoneNumber: "",
    };
  };

  validate = (values) => {
    // At the moment, we limit one gate can only have 1 CI lane and 1 CO lane.
    // This is done on purpose because R4G does not yet support multi-lane gates yet.
    // The limitation will be removed by https://sim.amazon.com/issues/Sesame-9201
    // with a feature flag until R4G supports mult-lane.
    const { t } = this.props;
    const labelError = !values.label;
    const purposeRequiredError = !values.purpose;
    const purposeUsedError =
      this.props.gateLane?.purpose !== values.purpose &&
      this.props.gateLanes.some((gateLane) => equalsIgnoreCase(gateLane.purpose, values.purpose));
    const kfbAccessPointIdError = !this.isKfBAccessPointIdValid(values.kfbAccessPointId);
    const r4gModeError = !values.r4gMode;
    const intercomPhoneNumberError = !this.isIntercomPhoneNumberValid(values.intercomPhoneNumber);
    return {
      ...(labelError && { label: t(keys.REQUIRED_ERROR_MESSAGE) }),
      ...(purposeRequiredError && {
        purposeRequired: t(keys.REQUIRED_ERROR_MESSAGE),
      }),
      ...(purposeUsedError && { purposeUsed: t(keys.GATE_TYPE_USED) }),
      ...(kfbAccessPointIdError && {
        kfbAccessPointId: t(keys.KFB_ACCESS_POINT_ID_INVALID),
      }),
      ...(r4gModeError && { r4gMode: t(keys.REQUIRED_ERROR_MESSAGE) }),
      ...(intercomPhoneNumberError && {
        intercomPhoneNumber: t(keys.INTERCOM_PHONE_NUMBER_INVALID),
      }),
    };
  };

  handleChange = (setFieldValue, fieldName) => (fieldValue) => {
    setFieldValue(fieldName, fieldValue);
    const { gateLanes } = this.props;
    if (fieldName === GATE_LANE_KEYS.LABEL && fieldValue) {
      const isLabelUsed = gateLanes.some((gateLane) =>
        equalsIgnoreCase(gateLane.label, fieldValue)
      );
      this.setState({ isLabelUsed });
    }
  };

  format = (values) => {
    // KfB access point id must be valid or not present at all
    const kfbAccessPointId = !isEmpty(values.kfbAccessPointId) ? values.kfbAccessPointId : null;
    // intercom phone number must be valid or not present at all
    const intercomPhoneNumber = !isEmpty(values.intercomPhoneNumber)
      ? parsePhoneNumber(values.intercomPhoneNumber).number
      : null;
    return {
      ...values,
      kfbAccessPointId: kfbAccessPointId,
      intercomPhoneNumber: intercomPhoneNumber,
    };
  };

  renderLabelAvailabilityCheck = (currentLabel) => {
    const { t } = this.props;
    const { isLabelUsed } = this.state;

    const previousLabel = this.props.gateLane?.label;
    const isLabelUnchanged = previousLabel && equalsIgnoreCase(currentLabel, previousLabel);

    if (!currentLabel || isLabelUnchanged) {
      return null;
    }

    return isLabelUsed ? (
      <Alert size="small" type="error">
        {t(keys.LABEL_USED)}
      </Alert>
    ) : (
      <Alert size="small" type="success">
        {t(keys.LABEL_AVAILABLE)}
      </Alert>
    );
  };

  isKfBAccessPointIdValid = (kfbAccessPointId) => {
    return (
      isEmpty(kfbAccessPointId) ||
      (isValidUUID(kfbAccessPointId) && getUUIDVersion(kfbAccessPointId) === 4)
    );
  };

  isIntercomPhoneNumberValid = (intercomPhoneNumber) => {
    return isEmpty(intercomPhoneNumber) || isValidPhoneNumber(intercomPhoneNumber);
  };

  render() {
    const { t, gateLane, add, edit, dismiss } = this.props;
    const { fields } = this.state;
    const initialValues = this.getInitialValues(gateLane);

    return (
      <Modal
        width="400px"
        bodySpacingInset="medium"
        title={gateLane ? t(keys.EDIT_GATE) : t(keys.ADD_GATE)}
        open={true}
      >
        <Formik
          initialValues={initialValues}
          // invalidate form on initialization for adding gate
          initialErrors={!gateLane && this.validate(initialValues)}
          validate={this.validate}
          onSubmit={(values) => (gateLane ? edit(this.format(values)) : add(this.format(values)))}
        >
          {({ values, errors, isValid, setFieldValue, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Column spacing="small" spacingInset="none none small none">
                <Row>
                  <Input
                    width="250px"
                    name={fields.label}
                    type="text"
                    label={`${t(keys.GATE_LABEL)}*`}
                    value={values.label}
                    onChange={this.handleChange(setFieldValue, fields.label)}
                  />
                  {this.renderLabelAvailabilityCheck(values.label)}
                </Row>
                <Row>
                  <Select
                    width="100%"
                    label={`${t(keys.GATE_TYPE)}*`}
                    placeholder={t(keys.SELECT_GATE_TYPE)}
                    value={values.purpose}
                    onChange={this.handleChange(setFieldValue, fields.purpose)}
                    error={!!errors.purposeUsed}
                    errorMessage={errors.purposeUsed}
                  >
                    {GATE_LANE_PURPOSE_LIST.map((purpose) => (
                      <SelectOption
                        key={purpose}
                        value={purpose}
                        label={t(
                          purpose === GATE_LANE_PURPOSE.CHECK_IN ? keys.ENTRY_GATE : keys.EXIT_GATE
                        )}
                      />
                    ))}
                  </Select>
                </Row>
                <Row>
                  <Input
                    width="100%"
                    name={fields.kfbAccessPointId}
                    type="text"
                    label={`${t(keys.GATE_KFB_ACCESS_POINT_ID)}`}
                    value={values.kfbAccessPointId}
                    onChange={this.handleChange(setFieldValue, fields.kfbAccessPointId)}
                    error={!!errors.kfbAccessPointId}
                    errorMessage={errors.kfbAccessPointId}
                  />
                </Row>
                <Row>
                  <Select
                    width="100%"
                    label={`${t(keys.GATE_R4G_MODE)}*`}
                    placeholder={t(keys.SELECT_R4G_MODE)}
                    value={values.r4gMode}
                    onChange={this.handleChange(setFieldValue, fields.r4gMode)}
                  >
                    {R4G_MODE_LIST.map((mode) => (
                      <SelectOption key={mode} value={mode} label={t(mode)} />
                    ))}
                  </Select>
                </Row>
                <Row>
                  <Input
                    data-testid="intercomPhoneNumberField"
                    width="100%"
                    name={fields.intercomPhoneNumber}
                    type="text"
                    label={`${t(keys.INTERCOM_PHONE_NUMBER)}`}
                    placeholder={"+X XXX XXX XXXX"}
                    value={values.intercomPhoneNumber}
                    onChange={this.handleChange(setFieldValue, fields.intercomPhoneNumber)}
                    error={!!errors.intercomPhoneNumber}
                    errorMessage={errors.intercomPhoneNumber}
                  />
                </Row>
              </Column>

              <Row alignmentHorizontal="right">
                <Button type="secondary" minWidth="80px" onClick={dismiss}>
                  {t(keys.CANCEL)}
                </Button>
                <Button
                  data-testid="closeButton"
                  type="primary"
                  minWidth="80px"
                  disabled={!isValid || this.state.isLabelUsed}
                  onClick={handleSubmit}
                >
                  {gateLane ? t(keys.CLOSE) : t(keys.ADD)}
                </Button>
              </Row>
            </form>
          )}
        </Formik>
      </Modal>
    );
  }
}

export { GateDialog };
export default withTranslation()(GateDialog);

GateDialog.propTypes = {
  gateLanes: PropTypes.arrayOf(gateLane).isRequired,
  gateLane: gateLane, // not required as is null when creating new object
  add: PropTypes.func.isRequired,
  edit: PropTypes.func.isRequired,
  dismiss: PropTypes.func.isRequired,
};
